import React, { useMemo, useState } from 'react';
import { Mail, Printer, MessageSquareText, Search, X, Eye, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';

const COLOUR = { sent: '#1a7f37', delivered: '#1a7f37', queued: '#8a5a00', mocked: '#8a5a00', failed: '#8a1f11', undelivered: '#8a1f11', received: 'var(--navy)' };
const Icon = ({ channel }) => (channel === 'letter' ? <Printer size={12} className="mt-0.5 shrink-0" /> : channel === 'sms' ? <MessageSquareText size={12} className="mt-0.5 shrink-0" /> : <Mail size={12} className="mt-0.5 shrink-0" />);

function Chips({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`} className="px-2 py-0.5 rounded text-[11px] font-title"
          style={value === v ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', border: '1px solid rgba(0,0,0,0.18)' }}>{l}</button>
      ))}
    </div>
  );
}

export function MessageViewer({ id, onClose }) {
  const { getCorrespondenceItem } = useAuth();
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { getCorrespondenceItem(id).then(setMsg).catch(() => setErr('Could not load this message.')); }, [id]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="message-viewer">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }} data-testid="message-viewer-subject">{msg?.subject || (err ? 'Message' : 'Loading…')}</h3>
            {msg && <p className="text-xs" style={{ color: 'var(--olive)' }} data-testid="message-viewer-meta">
              {msg.channel} · {msg.status} · {msg.kind === 'manual' ? `sent by ${msg.sent_by || 'admin'}` : msg.kind === 'inbound' ? `from ${msg.sent_by}` : 'system'} · {new Date(msg.created_at).toLocaleString()}
              {msg.to_email ? ` · to ${msg.to_email}` : msg.to_number ? ` · to ${msg.to_number}` : ''}{msg.batch_id ? ` · batch ${msg.batch_id}` : ''}
            </p>}
          </div>
          <button onClick={onClose} data-testid="message-viewer-close"><X size={18} /></button>
        </div>
        {err && <p className="text-sm" style={{ color: '#8a1f11' }}>{err}</p>}
        {!msg && !err && <Loader2 className="animate-spin" size={18} />}
        {msg && msg.html && <iframe title="message" srcDoc={msg.html} sandbox="" className="w-full bg-white rounded border" style={{ height: 460, borderColor: 'rgba(0,0,0,0.12)' }} data-testid="message-viewer-html" />}
        {msg && !msg.html && (msg.text ? (
          <pre className="text-sm whitespace-pre-wrap rounded p-3" style={{ background: 'rgba(0,0,0,0.03)' }} data-testid="message-viewer-text">{msg.text}</pre>
        ) : (
          <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="message-viewer-empty">The content of this message was not stored (recorded before message archiving was enabled).</p>
        ))}
        {msg?.from_batch && <p className="text-[11px]" style={{ color: 'var(--olive)' }}>Showing the batch message as written — merge fields were filled in per member when it was sent.</p>}
        {msg?.error && <p className="text-xs" style={{ color: '#8a1f11' }}>Error: {msg.error}</p>}
      </div>
    </div>
  );
}

export function CorrespondenceList({ items }) {
  const [q, setQ] = useState('');
  const [channel, setChannel] = useState('all');
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [viewing, setViewing] = useState(null);
  const all = useMemo(() => items || [], [items]);
  const shown = useMemo(() => all.filter((c) => {
    if (channel !== 'all' && (c.channel || 'email') !== channel) return false;
    if (status !== 'all' && !(status === 'failed' ? ['failed', 'undelivered'].includes(c.status) : status === 'sent' ? ['sent', 'delivered', 'queued', 'mocked', 'received'].includes(c.status) || c.channel === 'letter' : true)) return false;
    if (kind !== 'all' && (c.kind || 'system') !== kind) return false;
    if (q) { const s = `${c.subject || ''} ${c.sent_by || ''} ${c.batch_id || ''} ${c.status || ''}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }), [all, q, channel, status, kind]);

  return (
    <div data-testid="correspondence-list">
      <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Correspondence ({shown.length}{shown.length !== all.length ? ` of ${all.length}` : ''})</h4>
      {all.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--olive)' }}>No emails or letters recorded for this member yet.</p>
      ) : (
        <>
          <div className="space-y-1.5 mb-2">
            <div className="relative"><Search size={12} className="absolute left-2 top-2.5" style={{ color: 'var(--olive)' }} />
              <Input data-testid="correspondence-search" className="pl-6 h-7 text-xs" placeholder="Search subject, sender, batch…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="flex flex-wrap gap-2 items-center">
              <Chips value={channel} onChange={setChannel} testid="correspondence-filter-channel" options={[['all', 'All channels'], ['email', 'Email'], ['letter', 'Letter'], ['sms', 'SMS']]} />
              <Chips value={status} onChange={setStatus} testid="correspondence-filter-status" options={[['all', 'Any status'], ['sent', 'Sent'], ['failed', 'Failed']]} />
              <Chips value={kind} onChange={setKind} testid="correspondence-filter-kind" options={[['all', 'Any source'], ['manual', 'By admin'], ['system', 'System'], ['inbound', 'Inbound']]} />
            </div>
          </div>
          {shown.length === 0 ? <p className="text-xs" style={{ color: 'var(--olive)' }} data-testid="correspondence-empty">No messages match these filters.</p> : (
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {shown.map((c) => (
                <div key={c.id} className="flex items-start gap-2 text-xs" data-testid="correspondence-row">
                  <Icon channel={c.channel} />
                  <button type="button" onClick={() => setViewing(c.id)} className="truncate flex-1 text-left underline decoration-dotted" title={c.subject} style={{ color: 'var(--navy)' }} data-testid={`correspondence-view-${c.id}`}>
                    {c.direction === 'inbound' ? `↩ ${c.subject}` : c.subject || '(no subject)'}
                  </button>
                  <Eye size={11} className="mt-0.5 shrink-0" style={{ color: 'var(--olive)' }} />
                  <span className="whitespace-nowrap" style={{ color: 'var(--olive)' }}>{c.kind === 'manual' ? `by ${c.sent_by || 'admin'}` : c.kind === 'inbound' ? 'inbound' : 'system'}</span>
                  <span className="whitespace-nowrap" style={{ color: COLOUR[c.status] || 'var(--olive)' }}>{c.channel === 'letter' ? 'printed' : c.status}</span>
                  <span className="whitespace-nowrap" style={{ color: 'var(--olive)' }}>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {viewing && <MessageViewer id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
